import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  SendServiceRemindersRequest,
  SendServiceRemindersResult,
  ServiceDuesItem,
  ServiceDuesResponse,
  ServiceReminderVehicleResult,
} from '../../models/service-dues.interface';

@Injectable({ providedIn: 'root' })
export class ServiceDuesMocksService {
  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (req.method === 'POST' && req.url.includes('/send-reminders')) {
      return this.sendReminders(req.body as SendServiceRemindersRequest);
    }
    return this.getList(req);
  }

  private getList(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const url = new URL(req.url);
    // due-soon uses `withinDays`; the legacy dashboard endpoint uses `inDays`.
    const withinDays = parseInt(
      url.searchParams.get('withinDays') || url.searchParams.get('inDays') || '30',
      10
    );
    const page = parseInt(url.searchParams.get('page') || '1', 10);
    const limit = parseInt(url.searchParams.get('limit') || '20', 10);

    const allItems = this.generateMockData(withinDays);
    const total = allItems.length;
    const data = allItems.slice((page - 1) * limit, page * limit);

    const res: ServiceDuesResponse = {
      data,
      total,
      page,
      limit,
      totalPages: Math.ceil(total / limit),
    };
    return of(new HttpResponse({ status: 200, body: res })).pipe(delay(400));
  }

  private sendReminders(request: SendServiceRemindersRequest): Observable<HttpEvent<any>> {
    // Plausible outcome: email delivered, SMS skipped because SMS is disabled.
    const results: ServiceReminderVehicleResult[] = (request.vehicleIds ?? []).map((vehicleId) => ({
      vehicleId,
      email: { outcome: 'SENT' },
      sms: { outcome: 'SKIPPED', skipReason: 'SMS_DISABLED' },
    }));

    const response: SendServiceRemindersResult = {
      sentCount: results.length,
      skippedCount: 0,
      failedCount: 0,
      results,
    };
    return of(new HttpResponse({ status: 201, body: response })).pipe(delay(400));
  }

  private generateMockData(withinDays: number): ServiceDuesItem[] {
    const items: ServiceDuesItem[] = [];
    const today = new Date();
    const makes = ['Toyota', 'Ford', 'Mazda', 'Subaru', 'Honda'];
    const models = ['Corolla', 'Ranger', 'CX-5', 'Outback', 'Civic'];
    const serviceTypes: (['wof'] | ['service'] | ['wof', 'service'])[] = [
      ['wof'],
      ['service'],
      ['wof', 'service'],
    ];

    for (let i = 1; i <= 30; i++) {
      const dueDate = new Date(today);
      dueDate.setDate(dueDate.getDate() + Math.floor(Math.random() * withinDays) + 1);
      const makeIdx = i % makes.length;
      // Every 7th row has no contact details, to exercise the disabled checkbox.
      const contactable = i % 7 !== 0;
      items.push({
        vehicleId: 700 + i,
        customer: {
          name: `Customer ${i} Ltd`,
          contactName: `Contact Person ${i}`,
          email: contactable ? `customer${i}@example.co.nz` : null,
          mobilePhone: contactable ? `021 ${String(i).padStart(7, '0')}` : null,
        },
        vehicle: {
          rego: `ABC${String(i).padStart(3, '0')}`,
          make: makes[makeIdx],
          model: models[makeIdx],
        },
        upcomingServices: serviceTypes[i % serviceTypes.length] as any,
        dueDate: dueDate.toISOString().split('T')[0],
      });
    }

    // Sort by dueDate ascending (mirrors API ordering)
    return items.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  }
}
